
import React from 'react';
import { TrendingDown, TrendingUp, Circle, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useContracts } from '@/hooks/useContracts';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { Button } from '@/components/ui/button';
import { usePlanSelectDialog } from '@/contexts/PlanSelectDialogContext';

interface CostComparisonIndicatorProps {
  contractCost: number;
  supplierName: string;
  organizationNumber: string | null;
  contractTypeId: string;
  className?: string;
}

export function CostComparisonIndicator({
  contractCost,
  supplierName,
  organizationNumber,
  contractTypeId,
  className
}: CostComparisonIndicatorProps) {
  const { canUsePriceComparison } = useFeatureAccess();
  const { useAverageCostBySupplierQuery } = useContracts();
  const { openDialog } = usePlanSelectDialog();
  
  // Initialize the query with enabled flag from feature access
  const { 
    data: averageCostData, 
    isLoading,
    isError 
  } = useAverageCostBySupplierQuery(
    supplierName, 
    organizationNumber, 
    contractTypeId,
    canUsePriceComparison // Pass feature flag as the "enabled" option
  );
  
  // If the feature is disabled, render a small locked feature indicator
  if (!canUsePriceComparison) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center ml-1 cursor-help">
              <AlertCircle className="h-4 w-4 text-amber-500" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-white dark:bg-gray-900 p-3 max-w-[300px] shadow-lg border border-amber-200 dark:border-amber-800">
            <div className="space-y-3">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <h4>Upgrade to Access Price Comparison</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Your current plan doesn't include price comparison and currency conversion features.
              </p>
              <Button 
                size="sm"
                onClick={openDialog}
                className="w-full"
              >
                View Plans
              </Button>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Don't render anything if there's not enough data for comparison
  if (isLoading || isError || !averageCostData || averageCostData.count < 2) {
    return null;
  }

  const averageCost = averageCostData.average;
  const percentDifference = ((contractCost - averageCost) / averageCost) * 100;
  const isWithinThreshold = Math.abs(percentDifference) < 5; // Within 5% is considered "on par"

  // Format the currency for display
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  let tooltipText = '';
  let icon = null;
  
  if (isWithinThreshold) {
    tooltipText = `This cost is on par with the average (${formatCurrency(averageCost)}) 
                  based on ${averageCostData.count} similar contracts`;
    icon = <Circle className="h-4 w-4 text-gray-500" />;
  } else if (percentDifference > 0) {
    tooltipText = `This cost is ${Math.abs(percentDifference).toFixed(1)}% higher than 
                  the average (${formatCurrency(averageCost)}) 
                  based on ${averageCostData.count} similar contracts`;
    icon = <TrendingUp className="h-4 w-4 text-red-500" />;
  } else {
    tooltipText = `This cost is ${Math.abs(percentDifference).toFixed(1)}% lower than 
                  the average (${formatCurrency(averageCost)}) 
                  based on ${averageCostData.count} similar contracts`;
    icon = <TrendingDown className="h-4 w-4 text-green-500" />;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className={cn("inline-flex items-center ml-1", className)}>
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
